/* eslint-disable no-unused-vars */
import {
  ArrowBack,
  CalendarToday,
  DateRange,
  Inventory,
  Today
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import GenericSearchControls from '../components/common/GenericSearchControls';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import packageManagerService from '../services/package-manager-service';
import { PKG } from '../utils/package-fields';

export default function PackagesByDatePage() {
  const navigate = useNavigate();
  const { dateType } = useParams(); // Get dateType from URL params
  const [packages, setPackages] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });

  // Date configuration for display and icons
  const getDateConfig = (dateType) => {
    const configs = {
      Today: {
        icon: <Today />,
        color: 'primary',
        bgColor: '#162232',
        description: 'Packages processed today'
      },
      Yesterday: {
        icon: <CalendarToday />,
        color: 'secondary',
        bgColor: '#1E2235',
        description: 'Packages processed yesterday'
      },
      'This Week': {
        icon: <DateRange />,
        color: 'info',
        bgColor: '#121B2F',
        description: 'Packages processed this week'
      }
    };
    return (
      configs[dateType] || {
        icon: <Inventory />,
        color: 'default',
        bgColor: '#1A1D27',
        description: `Packages for: ${dateType}`
      }
    );
  };

  const dateConfig = getDateConfig(dateType);

  const fetchPackagesByDate = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await packageManagerService.getPackagesByDate(
          dateType,
          page,
          limit,
          {}, // empty filters for basic fetch
          sortBy,
          sortOrder
        );
        if (response.success) {
          setPackages(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPackages([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to fetch packages by date:', err);
      }
      setLoading(false);
    },
    [dateType, pagination.limit, sortBy, sortOrder]
  );

  // No standalone mount effect: fetchPackagesByDate's useCallback deps include
  // sortBy/sortOrder, so it refired on every sort change and, alongside the
  // else branch below, would fetch twice. The single effect below now owns
  // both mount and change, matching PackageManagerPage.

  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const filterObj = {};
        filters.forEach((f) => {
          if (f.field && f.value) filterObj[f.field] = f.value;
        });
        if (dateFilter !== 'default') {
          filterObj.date_filter = dateFilter;
        }
        if (searchValue) {
          filterObj.tracking_number = searchValue;
        }

        const response = await packageManagerService.getPackagesByDate(
          dateType,
          page,
          limit,
          filterObj,
          sortBy,
          sortOrder
        );

        if (response.success) {
          setPackages(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPackages([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to search packages:', err);
      }
      setLoading(false);
    },
    [dateType, searchValue, filters, dateFilter, pagination.limit, sortBy, sortOrder]
  );

  const debouncedSearch = useMemo(
    () => debounce((page, limit) => handleSearch(page, limit), 500),
    [handleSearch]
  );

  // Shared with PaginatedControls so paging uses the same search-vs-fetchAll
  // decision as this effect.
  const hasSearchCriteria =
    !!searchValue ||
    filters.length > 0 ||
    dateFilter !== 'default' ||
    sortBy !== 'default' ||
    sortOrder !== 'default';

  useEffect(() => {
    // Reset to page 1 unconditionally, then either search or fall back to the
    // unfiltered list. The `else` is the fix: with no branch here, clearing
    // the last filter left the previous filtered results frozen on screen
    // while the search UI showed empty — it looked like clearing did nothing.
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (hasSearchCriteria) {
      debouncedSearch(1, pagination.limit);
    } else {
      // Cancel any in-flight debounced search so a slower stale response
      // cannot land afterwards and re-apply the filter just cleared.
      debouncedSearch.cancel();
      fetchPackagesByDate(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch,
    hasSearchCriteria,
    fetchPackagesByDate
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'tracking_number':
        return 'Tracking Number';
      case 'batch_name':
        return 'Batch Name';
      case 'company_name':
        return 'Company Name';
      case 'uploaded_by':
        return 'Uploaded By';
      case 'direction':
        return 'Direction';
      case 'status':
        return 'Status';
      case 'weight':
        return 'Weight';
      default:
        return field;
    }
  };

  const filterOptions = [
    { value: 'tracking_number', label: 'Tracking Number' },
    { value: 'batch_name', label: 'Batch Name' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'uploaded_by', label: 'Uploaded By' },
    { value: 'direction', label: 'Direction' },
    { value: 'status', label: 'Status' },
    { value: 'weight', label: 'Weight' }
  ];

  const sortByList = [
    { value: 'id', label: 'Package ID' },
    { value: 'tracking_number', label: 'Tracking Number' },
    { value: 'batch_name', label: 'Batch Name' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'uploaded_by', label: 'Uploaded By' },
    { value: 'direction', label: 'Direction' },
    { value: 'status', label: 'Status' },
    { value: 'weight', label: 'Weight' },
    { value: 'created_at', label: 'Created Date' },
    { value: 'updated_at', label: 'Updated Date' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDirectionChipColor = (direction) => {
    const directionColors = {
      incoming: 'success',
      outgoing: 'warning',
      unclassified: 'error'
    };
    return directionColors[direction?.toLowerCase()] || 'default';
  };

  const getStatusChipColor = (status) => {
    const statusColors = {
      delivered: 'success',
      in_transit: 'info',
      pending: 'warning',
      lost: 'error',
      returned: 'secondary'
    };
    return statusColors[status?.toLowerCase()] || 'default';
  };

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        {/* Header with Breadcrumbs */}
        <Box mb={3}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/packages" color="inherit">
              Package Manager
            </Link>
            <Typography color="text.primary">{dateType} Packages</Typography>
          </Breadcrumbs>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{ mb: 1 }}
            >
              Back to Dashboard
            </Button>
          </Box>

          <Paper sx={{ p: 3, mb: 3, backgroundColor: dateConfig.bgColor }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ color: `${dateConfig.color}.main`, fontSize: 48 }}>
                {dateConfig.icon}
              </Box>
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  {dateType} Packages
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  {dateConfig.description}
                </Typography>
                <Chip
                  label={`Period: ${dateType}`}
                  color={dateConfig.color}
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
          </Paper>
        </Box>

        <div style={{ margin: '20px auto', width: '90%' }}>
          <GenericSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            searchFieldLabel="Tracking Number"
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            handleSearchClick={handleSearchClick}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
            conditionalFieldInputs={[
              { field: 'direction', label: 'Direction', options: [{ value: 'incoming', label: 'Incoming' }, { value: 'outgoing', label: 'Outgoing' }] }
            ]}
            resetAll={() => {
              setSearchValue('');
              setFilters([]);
              setSortBy('default');
              setSortOrder('default');
              setPagination({ currentPage: 1, totalPages: 1, limit: 20 });
              setShowAdvancedSearch(false);
              setFilterDraft({ field: '', value: '' });
              localStorage.removeItem('searchValue');
              localStorage.removeItem('advancedSearchFilters');
              fetchPackagesByDate();
            }}
          />

          {(searchValue || filters.length > 0) && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              formatFieldLabel={formatFieldLabel}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={packages.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchPackagesByDate}
            isSearchActive={hasSearchCriteria}
          />

          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6">Loading packages...</Typography>
            </Paper>
          ) : packages.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ color: 'text.secondary', fontSize: 48, mb: 2 }}>
                {dateConfig.icon}
              </Box>
              <Typography variant="h6" gutterBottom>
                No packages found for {dateType.toLowerCase()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchValue || filters.length > 0 || dateFilter !== 'default'
                  ? 'Try adjusting your search filters.'
                  : `There are currently no packages for "${dateType}".`}
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Batch Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packages.map((pkg) => (
                    <TableRow key={pkg.id}>
                      <TableCell>{pkg.id}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {pkg.tracking_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{pkg[PKG.BATCH_NAME] || 'N/A'}</TableCell>
                      <TableCell>{pkg.company_name || 'N/A'}</TableCell>
                      {/* Recipient = who scanned the package (its scanning user). */}
                      <TableCell>{pkg.user?.username || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={pkg.direction || 'Unknown'}
                          color={getDirectionChipColor(pkg.direction)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pkg.status || 'Unknown'}
                          color={getStatusChipColor(pkg.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{pkg.weight || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(pkg.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => navigate(`/package-details/${pkg.id}`)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={packages.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchPackagesByDate}
            isSearchActive={hasSearchCriteria}
          />
        </div>
      </div>
    </div>
  );
}
